import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Mail, Lock, AlertCircle, FileText } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { isMicrosoftAuthEnabled } from '../../../config/msalConfig';
import { MicrosoftSignInCancelled } from '../../../services/microsoftAuthService';

/**
 * Microsoft's brand mark, as required for "Sign in with Microsoft" buttons.
 * Inlined because Lucide has no vendor logos.
 */
const MicrosoftLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 21 21" aria-hidden="true" focusable="false">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithMicrosoft } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setIsMicrosoftLoading(true);

    try {
      await loginWithMicrosoft();
      navigate('/');
    } catch (err: any) {
      // Closing the popup is a deliberate action, not an error worth shouting about.
      if (!(err instanceof MicrosoftSignInCancelled)) {
        setError(err.message || 'Microsoft sign-in failed. Please try again.');
      }
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-100 via-beige-50 to-primary-50 dark:from-dark-bg dark:via-dark-bg-secondary dark:to-dark-bg-tertiary flex items-center justify-center px-4 transition-colors duration-300">
      {/* Elegant Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-400/10 dark:bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent-400/10 dark:bg-accent-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary-400/5 dark:bg-primary-500/3 rounded-full blur-2xl"></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        <div className="backdrop-blur-xl bg-white/80 dark:bg-dark-surface/90 rounded-3xl shadow-2xl dark:shadow-dark-lg border border-white/20 dark:border-dark-border p-10 transition-colors duration-300">
          {/* Elegant Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400 dark:from-accent-500 dark:via-accent-400 dark:to-accent-300 rounded-2xl mb-6 shadow-lg shadow-primary/20 dark:shadow-accent/20">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-display font-light text-gray-800 dark:text-white tracking-tight mb-2">BIB ContractHub</h2>
            <p className="text-gray-600 dark:text-gray-400 font-medium">Welcome back to your workspace</p>
          </div>

          {/* Elegant Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error-50/80 dark:bg-error-900/20 backdrop-blur-sm border border-error-200/30 dark:border-error-500/20 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error-700 dark:text-error-400 font-medium">{error}</p>
            </div>
          )}

          {/* Microsoft SSO - primary sign-in path when configured */}
          {isMicrosoftAuthEnabled && (
            <div className="mb-6">
              <button
                type="button"
                onClick={handleMicrosoftLogin}
                disabled={isMicrosoftLoading || isLoading}
                className="w-full bg-white dark:bg-dark-surface-raised text-gray-700 dark:text-gray-200 py-4 px-6 rounded-xl border-2 border-primary-200/50 dark:border-dark-border hover:bg-beige-50 dark:hover:bg-dark-surface hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all duration-200 font-semibold"
              >
                {isMicrosoftLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary-300 border-t-primary-600 dark:border-dark-border dark:border-t-accent-400 rounded-full animate-spin" />
                    <span>Waiting for Microsoft...</span>
                  </>
                ) : (
                  <>
                    <MicrosoftLogo className="w-5 h-5" />
                    <span>Sign in with Microsoft</span>
                  </>
                )}
              </button>

              <div className="flex items-center gap-4 mt-6">
                <div className="flex-1 h-px bg-gray-200/60 dark:bg-dark-border" />
                <span className="text-xs text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wide">
                  or
                </span>
                <div className="flex-1 h-px bg-gray-200/60 dark:bg-dark-border" />
              </div>
            </div>
          )}

          {/* Elegant Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-4 bg-beige-50/60 dark:bg-dark-bg-secondary/60 border-2 border-primary-200/50 dark:border-dark-border rounded-xl focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-400 dark:focus:border-accent-400 focus:outline-none backdrop-blur-sm transition-all duration-200 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-500"
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-4 bg-beige-50/60 dark:bg-dark-bg-secondary/60 border-2 border-primary-200/50 dark:border-dark-border rounded-xl focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-400 dark:focus:border-accent-400 focus:outline-none backdrop-blur-sm transition-all duration-200 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-500"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full group bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-primary/25 dark:hover:shadow-accent/25 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all duration-200 font-semibold"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login; 